import {
  AdminHeader,
  AdminRolePicker,
  AdminSpinner,
  Box,
  Disclaimer,
  Div,
  H5,
  HeaderIconWrapper,
  Img,
  ListSpinner,
  PlusIcon,
  QueryStatus,
  SearchInput,
  Tab,
  TabsWrapper,
  agentImage,
  handleSetUrlSearchParams,
  lightTheme,
  renderRole,
} from '@vgrubs/components';
import { User, UserRole } from 'components/src/types/user.types';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAdminUsersInfiniteScroll } from './hooks/useAdminUsersInfiniteScroll';
import { UsersAndAccessAddModal } from './UsersAndAccessAdd';
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';

enum UserActiveStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export const UsersAndAccess = () => {
  const [addUserModal, setAddUserModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('query') || '';

  const setRole = (value: UserRole[]) => {
    handleSetUrlSearchParams('roles', value.join(','), setSearchParams);
  };

  const roles = searchParams.get('roles') as string | null;

  const setActiveStatus = (status: UserActiveStatus) => {
    handleSetUrlSearchParams('status', status, setSearchParams);
  };

  const activeStatus = searchParams.get('status') || UserActiveStatus.ACTIVE;

  const {
    users,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    status,
    saveScrollPosition,
    refetch,
    nonDeletedCount,
    deletedCount,
    totalCount,
  } = useAdminUsersInfiniteScroll({
    initialQuery,
    roles: roles ? (roles.split(',') as UserRole[]) : [],
    deleted: activeStatus === UserActiveStatus.INACTIVE,
  });

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(e);
    handleSetUrlSearchParams('query', e.target.value, setSearchParams, true);
  };

  return (
    <>
      {(status === QueryStatus.PENDING || loading) && <AdminSpinner />}

      <AdminHeader title={`Users (${totalCount})`}>
        <Div $flex $gap="20px" $gapSM="8px">
          <AdminRolePicker
            onChange={setRole}
            value={roles ? (roles.split(',') as UserRole[]) : []}
          />
          <SearchInput
            value={inputValue}
            placeholder="Search User"
            onChange={onSearchChange}
            maxWidth="350px"
            maxWidthSM="160px"
          />
        </Div>
      </AdminHeader>
      <Div $flex $gap="20px" $gapSM="8px" $alignCenter $between>
        <TabsWrapper style={{ flex: 1, minWidth: 0 }}>
          <Tab
            $widthSM="calc(50% - 6px)"
            selected={activeStatus === UserActiveStatus.ACTIVE}
            onClick={() => setActiveStatus(UserActiveStatus.ACTIVE)}
          >
            Active ({nonDeletedCount})
          </Tab>
          <Tab
            $widthSM="calc(50% - 6px)"
            selected={activeStatus === UserActiveStatus.INACTIVE}
            onClick={() => setActiveStatus(UserActiveStatus.INACTIVE)}
          >
            Inactive ({deletedCount})
          </Tab>
        </TabsWrapper>
        <HeaderIconWrapper
          $opened={false}
          onClick={() => setAddUserModal(true)}
        >
          <PlusIcon />
        </HeaderIconWrapper>
      </Div>
      {users.map((user, i) => {
        return (
          <UserComponent
            key={i}
            user={user.user}
            onBeforeNavigate={saveScrollPosition}
          />
        );
      })}

      {users.length === 0 &&
        status !== QueryStatus.PENDING &&
        !isFetchingNextPage &&
        !loading && (
          <NoDataComponent
            image={agentImage}
            title="No Users"
            description="No users found for the selected filters."
          />
        )}

      <ListSpinner loading={isFetchingNextPage} observerRef={observerRef} />
      <UsersAndAccessAddModal
        modalIsOpen={addUserModal}
        setModalIsOpen={setAddUserModal}
        setLoading={setLoading}
        onCreateSuccess={refetch}
        refetch={refetch}
      />
    </>
  );
};

const UserComponent = ({
  user,
  onBeforeNavigate,
}: {
  user: User;
  onBeforeNavigate?: () => void;
}) => {
  const navigate = useNavigate();

  const handleUserClick = () => {
    onBeforeNavigate?.();
    if (user.role === 'salesAgent' || user.role === 'salesManager') {
      navigate(`/users/agent/${user.id}`);
    } else {
      navigate(`/users/${user.id}`, { state: user });
    }
  };

  return (
    <Box $pointer $mb="8" onClick={handleUserClick}>
      <H5 $mb={4}>{user.firstName + ' ' + user.lastName}</H5>
      <Disclaimer $color={lightTheme.lightGray} $m="0" $capitalize>
        {renderRole(user.role)}
      </Disclaimer>
    </Box>
  );
};
